import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Textarea,
} from "@heroui/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { createAbuseReport } from "../../services/api";
import { useTranslation } from "react-i18next";
import { CreateAbuseReportRequest, ReportAbusePopupProps } from "../../types/types";

export const ReportAbusePopup: React.FC<ReportAbusePopupProps> = ({
  isOpen,
  onClose,
  onSubmit,
  cardId,
  type,
  reportedTo,
}) => {
  const [reportDescription, setReportDescription] = useState("");
  const [reportError, setReportError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t, i18n } = useTranslation("reportAbusePopup");

  useEffect(() => {
    const savedLanguage = localStorage.getItem("i18nextLng") || "en";
    i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  useEffect(() => {
    if (!isOpen) {
      setReportDescription("");
      setReportError("");
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setReportDescription("");
    setReportError("");
    setIsSubmitting(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!reportDescription.trim()) {
      setReportError(t("errorReasonRequired"));
      return;
    }

    if (reportDescription.length > 500) {
      setReportError(t("errorReasonTooLong"));
      return;
    }

    const payload: CreateAbuseReportRequest = {
      reportedTo,
      reason: reportDescription.trim(),
      type: type,
      cardId: cardId || undefined,
    };

    setIsSubmitting(true);
    try {
      await createAbuseReport(payload);
      setReportError("");
      onSubmit(type, reportDescription.trim());
      toast.success(t("submit_success"));
      handleClose();
    } catch (error) {
      console.error("Failed to submit abuse report:", error);
      setReportError(t("errorReasonRequired"));
      toast.error(t("errorReasonRequired"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      isDismissable={!isSubmitting}
      backdrop="opaque"
      placement="center"
      size="md"
      classNames={{
        backdrop: "bg-black/40",
        wrapper: "items-center justify-center p-3 sm:p-4 z-50",
        base: "bg-white rounded-3xl shadow-[0px_16px_36px_0px_rgba(0,0,0,0.12)] w-full max-w-[calc(100vw-1.5rem)] sm:max-w-[440px] my-auto overflow-hidden font-['Plus_Jakarta_Sans',sans-serif] border border-gray-100",
        header: "px-6 pt-6 pb-2 shrink-0 border-none",
        body: "px-6 py-2 overflow-y-auto",
        footer: "px-6 pt-3 pb-6 shrink-0 border-none",
      }}
    >
      <ModalContent>
        <>
          <ModalHeader className="flex items-start gap-3.5">
            <div className="w-10 h-10 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center shrink-0 border border-red-100 shadow-sm">
              <ExclamationTriangleIcon className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-gray-900 leading-tight">
                {t("reportAbuse")}
              </h2>
              <p className="text-xs text-gray-500 mt-1 font-normal leading-relaxed">
                {t("reportSubtitle", "Help us keep our community safe. Please specify what violates our guidelines.")}
              </p>
            </div>
          </ModalHeader>

          <ModalBody className="space-y-2">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs sm:text-sm font-semibold text-gray-700">
                  {t("reasonLabel")}
                </label>
                <span className="text-[11px] text-gray-400 font-medium">
                  {reportDescription.length}{t("characterCount")}
                </span>
              </div>

              <Textarea
                value={reportDescription}
                onValueChange={(val) => {
                  setReportDescription(val);
                  if (reportError) setReportError("");
                }}
                minRows={4}
                maxRows={6}
                maxLength={500}
                placeholder={t("reasonPlaceholder")}
                variant="bordered"
                radius="lg"
                classNames={{
                  inputWrapper:
                    "border-gray-200 hover:border-gray-300 focus-within:!border-red-500 bg-gray-50/50 transition-colors shadow-none",
                  input:
                    "text-sm font-normal text-gray-900 placeholder:text-gray-400 font-['Plus_Jakarta_Sans',sans-serif]",
                }}
              />
            </div>

            {reportError && (
              <p className="text-xs text-red-500 font-medium leading-tight">
                {reportError}
              </p>
            )}
          </ModalBody>

          <ModalFooter className="flex items-center justify-end gap-2.5">
            <Button
              variant="light"
              size="sm"
              onPress={handleClose}
              isDisabled={isSubmitting}
              className="flex-1 sm:flex-none px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors font-['Plus_Jakarta_Sans',sans-serif]"
            >
              {t("cancel")}
            </Button>
            <Button
              color="danger"
              size="sm"
              onPress={handleSubmit}
              isLoading={isSubmitting}
              className="flex-1 sm:flex-none px-5 py-2 rounded-full text-xs sm:text-sm font-bold bg-red-600 hover:bg-red-700 text-white shadow-sm transition-colors font-['Plus_Jakarta_Sans',sans-serif]"
            >
              {t("submit")}
            </Button>
          </ModalFooter>
        </>
      </ModalContent>
    </Modal>
  );
};
